import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Orbit, Hash, Layers, CalendarDays, HeartHandshake, House } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { ConsultPromo } from "@/components/ConsultPromo";
import { ReviewsSection } from "@/components/reviews/ReviewsSection";
import { KundaliArt } from "@/components/horoscope/KundaliArt";

const Feature = ({ icon: Icon, title, text, to, cta, testid, art }) => (
  <Link to={to} data-testid={testid} className="tile-art card-sacred group block overflow-hidden transition-transform duration-300 hover:-translate-y-1">
    {art && <KundaliArt testid={`${testid}-image`} />}
    <div className="p-8">
    <Icon className="text-[#D4AF37] mb-5" size={28} strokeWidth={1.4} />
    <h3 className="text-xl sm:text-2xl text-[#F3DC8B] mb-3">{title}</h3>
    <p className="text-slate-400 leading-relaxed mb-6">{text}</p>
    <span className="text-sm text-[#E8C55A] group-hover:underline underline-offset-4">{cta} →</span>
    </div>
  </Link>
);

const Hero = () => {
  const { t } = useLang();
  return (
  <section className="relative overflow-hidden">
    <div className="absolute -right-40 top-0 w-[720px] h-[720px] glow-red" />
    <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-16 pb-20 lg:pt-24 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7 fade-up">
        <p className="eyebrow mb-6">{t("home.eyebrow")}</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.1] mb-6">
          <span className="gold-text sparkle-text">{t("home.h1a")}</span>
          <br />
          <span className="text-slate-100">{t("home.h1b")}</span>
        </h1>
        <p className="text-base md:text-lg text-slate-400 max-w-xl leading-relaxed mb-10 font-serif-soft">
          {t("home.text")}
        </p>
        <div className="flex flex-wrap gap-4">
          <Link to="/horoscope" className="btn-gold px-7 py-3.5" data-testid="hero-chart-cta">{t("cta.chart")}</Link>
          <Link to="/book" className="btn-ghost-gold px-7 py-3.5" data-testid="hero-book-cta">{t("cta.book")}</Link>
        </div>
      </div>
      <div className="lg:col-span-5 flex justify-center fade-up" style={{ animationDelay: ".15s" }}>
        <div className="relative">
          <div className="absolute inset-[-18px] rounded-t-full rounded-b-3xl border border-[#D4AF37]/25" style={{ borderStyle: "dashed" }} />
          <img src="/brand/astrologer.webp" alt="Shri Vidya Jyotish astrologer" fetchPriority="high" width="560" height="840" className="w-64 sm:w-80 aspect-[2/3] rounded-t-full rounded-b-3xl object-cover ring-1 ring-[#D4AF37]/50 shadow-[0_0_80px_rgba(185,28,28,0.35)]" data-testid="hero-astrologer-image" />
          <img src="/brand/logo-sm.webp" alt="Shri Vidya emblem" className="absolute -bottom-6 -left-6 w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover ring-2 ring-[#D4AF37]/70 shadow-[0_0_30px_rgba(212,175,55,0.35)]" data-testid="hero-logo" />
        </div>
      </div>
    </div>
  </section>
  );
};

export default function Home() {
  const { t, mr } = useLang();
  const navigate = useNavigate();
  const openTarot = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate("/today#daily-tarot");
  };
  return (
    <div>
      <Hero />
      <p className="font-deva text-center text-[#E8C55A]/90 text-base md:text-lg px-5 mb-10" data-testid="guru-shloka">
        || गुरू ब्रह्मा गुरू विष्णु, गुरु देवो महेश्वरा गुरु साक्षात परब्रह्म, तस्मै श्री गुरुवे नमः ||
      </p>
      <div className="divider-gold mx-auto max-w-5xl" />
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-20" data-testid="home-services-section">
        <p className="eyebrow mb-2">{t("home.svcEyebrow")}</p>
        <h2 className="text-3xl sm:text-4xl gold-text mb-8">{t("home.svcTitle")}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[
          ["astrology", Sparkles, `₹600 · 30 ${t("min")}`],
          ["vastu", House, "₹5000"],
          ["numerology", Hash, `₹600 · 30 ${t("min")}`],
          ["tarot", Layers, `₹150 ${t("perQ")}`],
          ["kundli_milan", HeartHandshake, `₹1000 · 30 ${t("min")}`],
        ].map(([id, Icon, price]) => (
          <Link key={id} to={`/book?service=${id}`} data-testid={`service-tile-${id}`} className="tile-art group block rounded-2xl border border-[#D4AF37]/20 bg-[#0F172A]/70 hover:border-[#D4AF37]/60 transition-colors duration-300">
            <img src={`/brand/tile-${id}.webp`} alt={t(`short.${id}`)} loading="lazy" className="w-full h-40 sm:h-44 object-cover" data-testid={`service-tile-${id}-image`} />
            <div className="p-6">
              <div className="flex items-center gap-2 font-display text-lg text-[#F3DC8B]"><Icon size={18} className="text-[#D4AF37]" />{t(`short.${id}`)}</div>
              <div className="text-sm text-slate-400 mt-1">{t(`with.${id}`)}</div>
              <div className="mt-3 font-mono-num text-sm text-[#FDE68A]">{price}</div>
              {id === "tarot" && (
                <button type="button" onClick={openTarot} data-testid="home-tarot-card-of-day-button"
                  className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/60 px-4 py-2 text-sm text-[#F3DC8B] hover:bg-[#D4AF37] hover:text-[#4a0505] transition-colors">
                  <Layers size={14} /> {mr ? "आजचे कार्ड काढा" : "Card of the Day"}
                </button>
              )}
            </div>
          </Link>
        ))}
        </div>
        <Link to="/kundli-milan" data-testid="home-kundli-milan-check-link" className="mt-6 card-sacred p-6 sm:p-8 flex flex-wrap items-center gap-5 hover:border-[#D4AF37]/60 transition-colors">
          <HeartHandshake size={32} className="text-[#D4AF37] shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <h2 className="text-xl sm:text-2xl gold-text">{mr ? "मोफत कुंडली मिलन तपासा (36 गुण)" : "Check Kundli Milan Free (36 Gunas)"}</h2>
            <p className="text-slate-400 text-sm mt-1">{mr ? "वर-वधूची जन्म माहिती भरा आणि अष्टकूट गुण मिलन, नाडी व भकूट दोष लगेच पहा." : "Enter the boy's and girl's birth details to see the Ashtakoota score with Nadi and Bhakoot dosha check instantly."}</p>
          </div>
          <span className="btn-gold px-6 py-3 text-center">{mr ? "कुंडली जुळवा" : "Match Kundli"}</span>
        </Link>
      </section>
      <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-16">
        <Link to="/today" data-testid="home-today-card" className="tile-art card-sacred-red p-8 sm:p-10 grid sm:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <p className="eyebrow mb-2">{t("home.todayEyebrow")}</p>
            <h2 className="text-2xl sm:text-3xl gold-text mb-2">{t("home.todayTitle")}</h2>
            <p className="text-slate-300">{t("home.todayText")}</p>
          </div>
          <span className="btn-gold px-6 py-3 text-center">{t("home.todayCta")}</span>
        </Link>
        <Link to="/today#vrat-calendar" data-testid="home-vrat-calendar-link" className="mt-6 card-sacred p-6 sm:p-8 flex flex-wrap items-center gap-5 hover:border-[#D4AF37]/60 transition-colors">
          <CalendarDays size={32} className="text-[#D4AF37] shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <h2 className="text-xl sm:text-2xl gold-text">{mr ? "या महिन्यातील व्रते व सण" : "This Month's Vrats & Festivals"}</h2>
            <p className="text-slate-400 text-sm mt-1">{mr ? "एकादशी, संकष्टी, पौर्णिमा, अमावास्या व सण – तिथी वेळा, पारणे व पूजा मुहूर्तासह." : "Ekadashi, Sankashti, Purnima, Amavasya and festivals – with Tithi times, Parana and Puja muhurat."}</p>
          </div>
          <span className="btn-ghost-gold px-6 py-3 text-center">{mr ? "दिनदर्शिका पहा" : "Open Calendar"}</span>
        </Link>
      </div>
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 grid md:grid-cols-2 gap-8 items-stretch">
        <Feature icon={Orbit} testid="feature-horoscope-card" to="/horoscope" cta={t("f1.cta")} title={t("f1.title")}
          text={t("f1.text")} art />
        <ConsultPromo stacked testid="home-consult-promo" />
      </section>
      <ReviewsSection />
    </div>
  );
}
